import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bot, MessageCircle, Send, Sparkles, X } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { useLayout } from "../../contexts/LayoutContext";
import { getAiConversation, listAiConversations, sendAiMessage } from "../../api/ai";
import { resolveFeatureSections } from "../../utils/featureSections";
import logger from "../../utils/logger.js";

function formatRelativeTime(value, locale = "en") {
  if (!value) return "";
  try {
    return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "";
  }
}

function MiniBubble({ message, isAr }) {
  const isAssistant = message?.role === "assistant";
  return (
    <article
      className={`ai-widget__bubble-row ${
        isAssistant ? "ai-widget__bubble-row--assistant" : "ai-widget__bubble-row--user"
      }`}
    >
      <div className={`ai-widget__bubble ${isAssistant ? "is-assistant" : "is-user"}`}>
        <div className="ai-widget__bubble-text">{message?.content || ""}</div>
        <div className="ai-widget__bubble-meta">
          <span>{isAssistant ? (isAr ? "المساعد" : "Assistant") : (isAr ? "أنت" : "You")}</span>
          {message?.created_at ? (
            <span>{formatRelativeTime(message.created_at, isAr ? "ar" : "en")}</span>
          ) : null}
        </div>
      </div>
    </article>
  );
}

export default function FloatingAiWidget() {
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const { settings, loadingSettings } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const isAr = lang === "ar";
  const sections = resolveFeatureSections(settings);

  const [open, setOpen] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const bottomRef = useRef(null);

  const hiddenRoutes = useMemo(
    () => ["/ai-assistant", "/login", "/register", "/forgot-password", "/reset-password"],
    []
  );

  const shouldHide = hiddenRoutes.some((path) => location.pathname.startsWith(path));

  useEffect(() => {
    if (!open) return;
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [open, messages, sending]);

  useEffect(() => {
    if (!open || !isLogged || bootstrapped) return;
    let active = true;

    async function bootstrap() {
      try {
        setLoading(true);
        setError("");
        const conversations = await listAiConversations(1);
        if (!active) return;

        if (conversations.length === 0) {
          setMessages([]);
          setActiveConversationId(null);
          setBootstrapped(true);
          return;
        }

        const conversationId = Number(conversations[0].id);
        const conversation = await getAiConversation(conversationId);
        if (!active) return;

        setActiveConversationId(conversationId);
        setMessages(Array.isArray(conversation?.messages) ? conversation.messages.slice(-8) : []);
        setBootstrapped(true);
      } catch (err) {
        logger.error("Floating AI widget bootstrap error", err);
        if (active) {
          setError(isAr ? "تعذر تحميل المحادثة." : "Unable to load conversation.");
          setBootstrapped(true);
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    bootstrap();
    return () => { active = false; };
  }, [open, isLogged, bootstrapped, isAr]);

  async function handleSend(event) {
    event.preventDefault();
    const trimmed = message.trim();
    if (!trimmed || sending) return;

    try {
      setSending(true);
      setError("");
      const response = await sendAiMessage({
        message: trimmed,
        locale: isAr ? "ar" : "en",
        conversationId: activeConversationId,
      });

      const conversationId = Number(response?.conversation_id || activeConversationId || 0);
      if (!conversationId) throw new Error("Missing conversation id");

      const conversation = await getAiConversation(conversationId);
      setActiveConversationId(conversationId);
      setMessages(Array.isArray(conversation?.messages) ? conversation.messages.slice(-8) : []);
      setMessage("");
      setBootstrapped(true);
    } catch (err) {
      logger.error("Floating AI widget send error", err);
      setError(isAr ? "تعذر إرسال الرسالة الآن." : "Unable to send your message right now.");
    } finally {
      setSending(false);
    }
  }

  if (loadingSettings || shouldHide || !sections.aiChat) {
    return null;
  }

  return (
    <div className={`ai-widget ${open ? "is-open" : ""}`} dir={isAr ? "rtl" : "ltr"}>
      {open ? (
        <section className="ai-widget__panel">
          <header className="ai-widget__header">
            <div className="ai-widget__header-main">
              <span className="ai-widget__icon-shell">
                <Sparkles size={16} />
              </span>
              <div>
                <h3>{isAr ? "المساعد الذكي" : "AI Assistant"}</h3>
                <p>
                  {isLogged
                    ? (isAr ? "نافذة سريعة للمساعدة والبحث داخل المنصة." : "A quick help window for platform questions.")
                    : (isAr ? "سجّل الدخول لبدء المحادثة." : "Sign in to start chatting.")}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="ai-widget__close"
              onClick={() => setOpen(false)}
              aria-label={isAr ? "إغلاق" : "Close"}
            >
              <X size={18} />
            </button>
          </header>

          <div className="ai-widget__body">
            {!isLogged ? (
              <div className="ai-widget__state">
                <Bot size={24} />
                <h4>{isAr ? "المساعد يحتاج تسجيل دخول" : "Sign in required"}</h4>
                <p>
                  {isAr
                    ? "سجّل الدخول لتسأل عن الطلبات والمنتجات والسياسات من نافذة سريعة."
                    : "Sign in to ask about orders, products, and policies from a compact widget."}
                </p>
                <button
                  type="button"
                  className="ai-widget__primary"
                  onClick={() => navigate("/login")}
                >
                  {isAr ? "تسجيل الدخول" : "Go to login"}
                </button>
              </div>
            ) : loading ? (
              <div className="ai-widget__state">
                <div className="spinner-border spinner-border-sm text-primary" role="status" />
                <p>{isAr ? "جارِ تحميل المحادثة..." : "Loading conversation..."}</p>
              </div>
            ) : (
              <>
                {messages.length === 0 ? (
                  <div className="ai-widget__state">
                    <MessageCircle size={24} />
                    <h4>{isAr ? "ابدأ سؤالًا سريعًا" : "Start a quick question"}</h4>
                    <p>
                      {isAr
                        ? "مثلاً: أين طلبي؟ أو ما سياسة الشحن؟"
                        : "For example: Where is my order? or What is the shipping policy?"}
                    </p>
                  </div>
                ) : (
                  <div className="ai-widget__messages">
                    {messages.map((item) => (
                      <MiniBubble
                        key={item.id || `${item.role}-${item.sequence}-${item.created_at || ""}`}
                        message={item}
                        isAr={isAr}
                      />
                    ))}
                    <div ref={bottomRef} />
                  </div>
                )}

                {error ? <div className="ai-widget__error">{error}</div> : null}

                <form className="ai-widget__composer" onSubmit={handleSend}>
                  <textarea
                    value={message}
                    onChange={(event) => setMessage(event.target.value)}
                    rows={2}
                    disabled={sending}
                    placeholder={isAr ? "اكتب سؤالك ..." : "Ask a quick question..."}
                  />
                  <div className="ai-widget__actions">
                    <button
                      type="button"
                      className="ai-widget__secondary"
                      onClick={() => navigate("/ai-assistant")}
                    >
                      {isAr ? "فتح كامل" : "Open full"}
                    </button>
                    <button
                      type="submit"
                      className="ai-widget__primary"
                      disabled={sending || message.trim() === ""}
                    >
                      <Send size={15} />
                      <span>{sending ? (isAr ? "جارٍ..." : "Sending") : (isAr ? "إرسال" : "Send")}</span>
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </section>
      ) : null}

      <button
        type="button"
        className="ai-widget__launcher"
        onClick={() => setOpen((value) => !value)}
        aria-label={isAr ? "فتح المساعد الذكي" : "Open AI assistant"}
      >
        <span className="ai-widget__launcher-icon">
          <Bot size={20} />
        </span>
        <span className="ai-widget__launcher-copy">
          <strong>{isAr ? "المساعد الذكي" : "AI Assistant"}</strong>
          <small>{isAr ? "نافذة سريعة" : "Quick help"}</small>
        </span>
      </button>
    </div>
  );
}